import { icon } from "../kit/components.ts";
import { h } from "../kit/dom.ts";
import type { KitIcon } from "../kit/kit-model.ts";
import type { VitalsView } from "./vitals-model.ts";

interface Bar {
  root: HTMLElement;
  fill: HTMLElement;
  value: HTMLElement;
  /** O que está no DOM (F109): só escreve quando muda, e a barra anda por `transform` (sem layout). */
  shown: number;
  text: string;
  now: string;
}

function bar(kind: string, label: string): Bar {
  const fill = h("div", { class: "fill" });
  const value = h("span", { class: "value" });
  // O ícone (SVG, F113) diz qual é a barra a quem vê; o nome fica para o leitor de tela.
  const root = h("div", { class: `bar ${kind}`, "data-bar": kind, role: "meter", "aria-label": label, "aria-valuemin": 0, "aria-valuemax": 100 }, icon(`vital-${kind}` as KitIcon), fill, h("span", { class: "bar-nome" }, label), value);
  return { root, fill, value, shown: Number.NaN, text: "", now: "" };
}

/** A fração na barra (o preenchimento é `scaleX` a partir da esquerda), o número e o `aria-valuenow`. */
function paint(b: Bar, fraction: number, text: string): void {
  const f = Math.round(Math.min(1, Math.max(0, fraction)) * 1000) / 1000;
  if (f !== b.shown) {
    b.shown = f;
    b.fill.style.transform = `scaleX(${f})`;
  }
  if (text !== b.text) b.value.textContent = b.text = text;
  const now = String(Math.round(f * 100));
  if (now !== b.now) b.root.setAttribute("aria-valuenow", (b.now = now));
}

/**
 * Atributos na tela: Vida (com o Patuá por cima), Fome e Fôlego, prato ativo, anel do canal de
 * comer em volta da mira, vinheta de perigo (vida baixa ou afogando) e o aviso de desmaio. Desde a F109, o
 * cartão do jogador do HUD (em cima à esquerda): o medalhão com a forma da classe e o número do jogador,
 * e as barras. Chamado por quadro: cada escrita no DOM só acontece quando o valor muda.
 */
export class VitalsHud {
  /** O cartão (`#vitais`): a cena o põe na coluna da esquerda do HUD. */
  readonly element: HTMLElement;
  private readonly hp: Bar;
  private readonly patua: HTMLElement;
  private readonly hunger: Bar;
  private readonly stamina: Bar;
  private readonly effect: HTMLElement;
  private readonly medal: HTMLElement;
  private readonly glyph: HTMLElement;
  private readonly number: HTMLElement;
  private readonly ring: HTMLElement;
  private readonly vignette: HTMLElement;
  private readonly fainted: HTMLElement;
  private patuaWasBroken = false;
  private shaded = false;
  private desat = 0;
  private patuaShown = Number.NaN;
  private effectText: string | undefined;
  private vignetteClass = "";
  private eatingShown = Number.NaN;
  private deadShown: boolean | undefined;
  private identity = { glyph: "", className: "", n: 0 };

  constructor(parent: HTMLElement) {
    this.hp = bar("vida", "Vida");
    this.patua = h("div", { class: "patua", title: "Égide" });
    this.hp.root.insertBefore(this.patua, this.hp.fill.nextSibling);
    this.hunger = bar("fome", "Fome");
    this.stamina = bar("folego", "Fôlego");
    this.effect = h("div", { class: "efeito" });
    this.effect.hidden = true;
    // O medalhão (F109): a forma da classe e o número do jogador, como nos quadros do grupo.
    this.glyph = h("span", { class: "glifo" });
    this.number = h("span", { class: "numero" });
    this.medal = h("div", { class: "medalhao", "aria-hidden": "true" }, this.glyph, this.number);
    this.medal.hidden = true;
    this.element = h("div", { id: "vitais", "aria-label": "Atributos" }, this.medal, this.hp.root, this.hunger.root, this.stamina.root, this.effect);
    this.ring = h("div", { id: "comendo", "aria-hidden": "true" });
    this.vignette = h("div", { id: "vinheta", "aria-hidden": "true" });
    this.fainted = h("div", { id: "morte", role: "alert" });
    parent.append(this.element, this.ring, this.vignette, this.fainted);
  }

  /** A classe e o número do jogador no medalhão (F109); a cena chama a cada atualização (só escreve o que mudou). */
  setIdentity(glyph: string, className: string, n: number): void {
    const id = this.identity;
    if (glyph === id.glyph && className === id.className && n === id.n) return;
    this.identity = { glyph, className, n };
    this.glyph.textContent = glyph;
    this.number.textContent = String(n);
    this.medal.title = `${className}, jogador ${n}`;
    this.medal.hidden = false;
  }

  update(view: VitalsView): void {
    paint(this.hp, view.hp, view.hpText);
    // Mesma escala da Vida: 25 de Patuá ocupam um quarto da barra.
    const patua = Math.round(Math.min(1, Math.max(0, view.patua * 0.25)) * 1000) / 1000;
    if (patua !== this.patuaShown) {
      this.patuaShown = patua;
      this.patua.style.transform = `scaleX(${patua})`;
    }
    this.hp.root.classList.toggle("baixa", view.lowHp);
    paint(this.hunger, view.hunger, view.hungerText);
    this.hunger.root.classList.toggle("baixa", view.lowHunger || view.starving);
    paint(this.stamina, view.stamina, view.submerged ? "mergulhado" : view.exhausted ? "exausto" : "");
    this.stamina.root.classList.toggle("baixa", view.exhausted || view.drowning);
    const effect = view.effect ?? (view.starving ? "Com fome: sem recuperar Vida" : "");
    if (effect !== this.effectText) {
      this.effectText = effect;
      this.effect.textContent = effect;
      this.effect.hidden = !effect;
    }
    // Quebra do Patuá: um lampejo (o som chega na F29).
    if (view.patuaBroken && !this.patuaWasBroken) {
      this.hp.root.classList.remove("quebrou");
      void this.hp.root.offsetWidth;
      this.hp.root.classList.add("quebrou");
    }
    this.patuaWasBroken = view.patuaBroken;
    if (view.eating !== this.eatingShown) {
      this.eatingShown = view.eating;
      this.ring.style.display = view.eating > 0 ? "block" : "none";
      this.ring.style.setProperty("--p", String(view.eating));
    }
    const shade = view.drowning ? "afogando" : view.criticalHp ? "critico" : view.lowHp ? "perigo" : "";
    if (shade !== this.vignetteClass) {
      // O lampejo do golpe (`golpe`) fica por cima da troca.
      const flashing = this.vignette.classList.contains("golpe");
      this.vignetteClass = shade;
      this.vignette.className = shade;
      if (flashing) this.vignette.classList.add("golpe");
    }
    this.shaded = shade !== "";
    this.desat = view.drowning ? 0.7 : view.criticalHp ? 0.85 : view.lowHp ? 0.45 : 0;
    if (view.dead !== this.deadShown) {
      this.deadShown = view.dead;
      this.fainted.style.display = view.dead ? "block" : "none";
    }
    if (this.fainted.textContent !== view.deathMessage) this.fainted.textContent = view.deathMessage;
  }

  /**
   * Vinheta que fica na tela (vida baixa ou crítica, afogando; o lampejo do golpe não conta): a do
   * pós-processamento (F52) dá lugar a ela.
   */
  get vignetteOn(): boolean {
    return this.shaded;
  }

  /** Dessaturação pedida ao pós (F75, 0 a 1): vida baixa, crítica, afogando. O vermelho é só do chefe. */
  get desaturation(): number {
    return this.desat;
  }

  /** Lampejo (neutro, F75) de golpe recebido. */
  flash(): void {
    this.vignette.classList.remove("golpe");
    void this.vignette.offsetWidth;
    this.vignette.classList.add("golpe");
  }

  remove(): void {
    this.element.remove();
    this.ring.remove();
    this.vignette.remove();
    this.fainted.remove();
  }
}
